import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Database, Search, Filter, Calendar, DollarSign, MapPin, Building2, Tag, X } from 'lucide-react';
import RegionProvinceSelector from '../components/RegionProvinceSelector';
import CPVTreeSelector from '../components/CPVTreeSelector';
import SOASelector from '../components/SOASelector';
import API_BASE_URL from '../config/api';
import { COLORS } from '../config/theme';

const StoricoGarePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitialMount = useRef(true);
  const skipInitialFetchRef = useRef(false);
  const statsCacheRef = useRef(new Map());
  const statsDebounceRef = useRef(null);
  const statsInFlightKeyRef = useRef(null);
  const currentStatsKeyRef = useRef('');
  const STATS_TTL_MS = 60_000;
  const avvisiInFlightKeyRef = useRef(null);
  const avvisiAbortRef = useRef(null);
  
  // ========================================
  // STATE
  // ========================================
  const [avvisi, setAvvisi] = useState([]);  // 🔥 CAMBIATO: da lotti a avvisi
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [hydrated, setHydrated] = useState(false);
  const [appliedNonce, setAppliedNonce] = useState(0);
  
  // Filtri
  const [filters, setFilters] = useState({
    search_text: '',
    date_from: '',
    date_to: '',
    published_from: '',  // 🔥 NUOVO: Filtro data pubblicazione da
    published_to: '',     // 🔥 NUOVO: Filtro data pubblicazione a
    value_min: '',
    value_max: '',
    regioni: [],
    province: [],
    cpv_codes: [],
    soa_codes: [],    // 🔥 NUOVO: Filtro SOA
    status: ''
  });
  
  // Paginazione
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total: 0,
    total_pages: 0
  });
  
  // Funzione per salvare i filtri nell'URL
  const updateUrlParams = (newFilters, newPage = pagination.page) => {
    const params = new URLSearchParams();
    
    if (newPage > 1) params.set('page', newPage);
    if (newFilters.search_text) params.set('search_text', newFilters.search_text);
    if (newFilters.date_from) params.set('date_from', newFilters.date_from);
    if (newFilters.date_to) params.set('date_to', newFilters.date_to);
    if (newFilters.published_from) params.set('published_from', newFilters.published_from);
    if (newFilters.published_to) params.set('published_to', newFilters.published_to);
    if (newFilters.value_min) params.set('value_min', newFilters.value_min);
    if (newFilters.value_max) params.set('value_max', newFilters.value_max);
    if (newFilters.regioni && newFilters.regioni.length > 0) params.set('regioni', newFilters.regioni.join(','));
    if (newFilters.province && newFilters.province.length > 0) params.set('province', newFilters.province.join(','));
    if (newFilters.cpv_codes && newFilters.cpv_codes.length > 0) params.set('cpv_codes', newFilters.cpv_codes.join(','));
    if (newFilters.soa_codes && newFilters.soa_codes.length > 0) params.set('soa_codes', newFilters.soa_codes.join(','));
    if (newFilters.status) params.set('status', newFilters.status);
    
    setSearchParams(params, { replace: true });
  };
  
  // Ripristina filtri dai parametri URL al mount
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      
      const urlFilters = {
        search_text: searchParams.get('search_text') || '',
        date_from: searchParams.get('date_from') || '',
        date_to: searchParams.get('date_to') || '',
        published_from: searchParams.get('published_from') || '',
        published_to: searchParams.get('published_to') || '',
        value_min: searchParams.get('value_min') || '',
        value_max: searchParams.get('value_max') || '',
        regioni: searchParams.get('regioni') ? searchParams.get('regioni').split(',') : [],
        province: searchParams.get('province') ? searchParams.get('province').split(',') : [],
        cpv_codes: searchParams.get('cpv_codes') ? searchParams.get('cpv_codes').split(',') : [],
        soa_codes: searchParams.get('soa_codes') ? searchParams.get('soa_codes').split(',') : [],
        status: searchParams.get('status') || ''
      };
      
      const page = parseInt(searchParams.get('page')) || 1;
      
      // Imposta i filtri solo se ci sono parametri URL
      const hasUrlParams = Array.from(searchParams.keys()).length > 0;
      if (hasUrlParams) {
        // Se esiste uno snapshot (salvato quando si clicca "Dettagli"), ripristina risultati identici.
        const snapshotKey = `storico_snapshot:${searchParams.toString()}`;
        const rawSnapshot = sessionStorage.getItem(snapshotKey);
        if (rawSnapshot) {
          try {
            const snapshot = JSON.parse(rawSnapshot);
            if (snapshot && Array.isArray(snapshot.avvisi) && snapshot.filters && snapshot.pagination) {
              setAvvisi(snapshot.avvisi);
              setFilters(snapshot.filters);
              setPagination(prev => {
                const next = snapshot.pagination;
                if (!next) return prev;
                if (
                  prev.page === next.page &&
                  prev.page_size === next.page_size &&
                  prev.total === next.total &&
                  prev.total_pages === next.total_pages
                ) {
                  return prev;
                }
                return next;
              });
              skipInitialFetchRef.current = true;

              // Ripristina lo scroll (dopo il render)
              const y = typeof snapshot.scrollY === 'number' ? snapshot.scrollY : 0;
              setTimeout(() => window.scrollTo(0, y), 0);
            } else {
              // Snapshot invalido: fallback su URL
              setFilters(urlFilters);
              setPagination(prev => (prev.page === page ? prev : { ...prev, page }));
            }
          } catch (_e) {
            // Snapshot non parsabile: fallback su URL
            setFilters(urlFilters);
            setPagination(prev => (prev.page === page ? prev : { ...prev, page }));
          } finally {
            // Evita snapshot stale su navigazioni successive
            sessionStorage.removeItem(snapshotKey);
          }
        } else {
          setFilters(urlFilters);
          setPagination(prev => (prev.page === page ? prev : { ...prev, page }));
        }
      }

      setHydrated(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo al mount

  // ========================================
  // CARICAMENTO DATI
  // ========================================

  const getStatsKey = () => {
    const params = new URLSearchParams();
    if (filters.search_text) params.append('search_text', filters.search_text);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.published_from) params.append('published_from', filters.published_from);
    if (filters.published_to) params.append('published_to', filters.published_to);
    if (filters.value_min) params.append('value_min', String(filters.value_min));
    if (filters.value_max) params.append('value_max', String(filters.value_max));
    if (filters.regioni?.length) params.append('regioni', filters.regioni.join(','));
    if (filters.province?.length) params.append('province', filters.province.join(','));
    if (filters.cpv_codes?.length) params.append('cpv_codes', filters.cpv_codes.join(','));
    if (filters.soa_codes?.length) params.append('soa_codes', filters.soa_codes.join(','));
    if (filters.status) params.append('status', filters.status);
    return params.toString();
  };

  const getAvvisiKey = () => {
    const params = new URLSearchParams();
    params.set('page', String(pagination.page));
    params.set('page_size', String(pagination.page_size));
    if (filters.search_text) params.append('search_text', filters.search_text);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.published_from) params.append('published_from', filters.published_from);
    if (filters.published_to) params.append('published_to', filters.published_to);
    if (filters.value_min) params.append('value_min', filters.value_min);
    if (filters.value_max) params.append('value_max', filters.value_max);
    if (filters.regioni?.length) params.append('regioni', filters.regioni.join(','));
    if (filters.province?.length) params.append('province', filters.province.join(','));
    if (filters.cpv_codes?.length) params.append('cpv_codes', filters.cpv_codes.join(','));
    if (filters.soa_codes?.length) params.append('soa_codes', filters.soa_codes.join(','));
    if (filters.status) params.append('status', filters.status);
    return params.toString();
  };

  const statsKey = useMemo(() => getStatsKey(), [
    filters.search_text,
    filters.date_from,
    filters.date_to,
    filters.published_from,
    filters.published_to,
    filters.value_min,
    filters.value_max,
    (filters.regioni || []).join(','),
    (filters.province || []).join(','),
    (filters.cpv_codes || []).join(','),
    (filters.soa_codes || []).join(','),
    filters.status
  ]);

  useEffect(() => {
    currentStatsKeyRef.current = statsKey;
  }, [statsKey]);
  
  useEffect(() => {
    if (!hydrated) return;
    // Se abbiamo ripristinato uno snapshot, NON rifare fetch al mount: deve restare identico.
    if (skipInitialFetchRef.current) {
      skipInitialFetchRef.current = false;
      return;
    }

    fetchAvvisi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated, pagination.page, appliedNonce]); // Ricarica quando cambia pagina o filtri applicati

  const fetchAvvisi = async () => {
    let key = '';

    try {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        toast.error('Token non trovato. Effettua il login.');
        window.location.href = '/login';
        return;
      }

      key = getAvvisiKey();
      console.log('🔎 FETCH AVVISI', { key, filters, page: pagination.page });
      if (avvisiInFlightKeyRef.current === key) return;

      if (avvisiAbortRef.current) {
        avvisiAbortRef.current.abort();
      }
      const controller = new AbortController();
      avvisiAbortRef.current = controller;
      avvisiInFlightKeyRef.current = key;
      setLoading(true);

      const response = await axios.get(
        `${API_BASE_URL}/storico-gare/?${key}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          signal: controller.signal
        }
      );

      if (avvisiInFlightKeyRef.current !== key) return;

      setAvvisi(response.data.avvisi);  // 🔥 CAMBIATO: da lotti a avvisi
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        total_pages: response.data.total_pages
      }));

      console.log('✅ Avvisi caricati:', response.data);
      
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        return;
      }
      console.error('❌ Errore nel caricamento avvisi:', error);
      
      if (error.response?.status === 401) {
        toast.error('Sessione scaduta. Effettua il login.');
        localStorage.removeItem('access_token');
        window.location.href = '/login';
      } else {
        toast.error('Errore nel caricamento dei dati');
      }
    } finally {
      if (avvisiInFlightKeyRef.current === key) {
        avvisiInFlightKeyRef.current = null;
        setLoading(false);
      }
    }
  };

  const fetchStats = async (key) => {
    const token = localStorage.getItem('access_token');
    if (!token) return;

    if (statsCacheRef.current.has(key)) {
      const cached = statsCacheRef.current.get(key);
      if (cached && (Date.now() - cached.ts) < STATS_TTL_MS) {
        setStats(cached.data);
        return;
      }
      statsCacheRef.current.delete(key);
    }

    if (statsInFlightKeyRef.current === key) return;
    statsInFlightKeyRef.current = key;

    try {
      const url = key
        ? `${API_BASE_URL}/storico-gare/stats?${key}`
        : `${API_BASE_URL}/storico-gare/stats`;

      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      statsCacheRef.current.set(key, { ts: Date.now(), data: response.data });
      if (currentStatsKeyRef.current === key) {
        setStats(response.data);
      }
      console.log('📊 Statistiche caricate:', response.data);
      
    } catch (error) {
      console.error('❌ Errore nel caricamento statistiche:', error);
    } finally {
      statsInFlightKeyRef.current = null;
    }
  };

  const fetchStatsDebounced = (key) => {
    if (statsDebounceRef.current) clearTimeout(statsDebounceRef.current);
    statsDebounceRef.current = setTimeout(() => {
      fetchStats(key);
    }, 350);
  };

  useEffect(() => {
    if (!hydrated) return;
    fetchStatsDebounced(statsKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated, statsKey]);

  useEffect(() => {
    return () => {
      if (statsDebounceRef.current) clearTimeout(statsDebounceRef.current);
      if (avvisiAbortRef.current) avvisiAbortRef.current.abort();
    };
  }, []);

  // ========================================
  // GESTIONE FILTRI
  // ========================================
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };

  const handleApplyFilters = () => {
    // Reset alla pagina 1 quando si applicano filtri
    const newPage = 1;
    setPagination(prev => ({ ...prev, page: newPage }));
    updateUrlParams(filters, newPage);
    setAppliedNonce(n => n + 1);
    toast.info('Filtri applicati');
  };

  const handleResetFilters = () => {
    const resetFilters = {
      search_text: '',
      date_from: '',
      date_to: '',
      published_from: '',  // 🔥 NUOVO
      published_to: '',   // 🔥 NUOVO
      value_min: '',
      value_max: '',
      regioni: [],
      province: [],
      cpv_codes: [],
      soa_codes: [],    // Reset SOA
      status: ''
    };
    setFilters(resetFilters);
    setPagination(prev => (prev.page === 1 ? prev : { ...prev, page: 1 }));
    updateUrlParams(resetFilters, 1);

    toast.info('Filtri resettati');
  };
  
  // 🔥 NUOVO: Handler per regioni e province
  const handleRegionsChange = (regioni) => {
    setFilters(prev => ({
      ...prev,
      regioni
    }));
  };

  const handleProvincesChange = (province) => {
    setFilters(prev => ({
      ...prev,
      province
    }));
  };

  // Handler per codici CPV
  const handleCPVCodesChange = (cpv_codes) => {
    setFilters(prev => ({
      ...prev,
      cpv_codes
    }));
  };

  // 🔥 NUOVO: Handler per codici SOA
  const handleSOACodesChange = (soa_codes) => {
    setFilters(prev => ({
      ...prev,
      soa_codes
    }));
  };

  // ========================================
  // PAGINAZIONE
  // ========================================
  
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      setPagination(prev => (prev.page === newPage ? prev : { ...prev, page: newPage }));
      updateUrlParams(filters, newPage);
    }
  };

  // ========================================
  // FORMATTAZIONE
  // ========================================
  
  const formatCurrency = (value) => {
    if (!value) return 'N/D';
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/D';
    try {
      // Gestisce sia stringhe che oggetti Date
      const date = dateString instanceof Date ? dateString : new Date(dateString);
      
      // Verifica se la data è valida
      if (isNaN(date.getTime())) {
        console.warn('Data non valida:', dateString);
        return 'N/D';
      }
      
      return new Intl.DateTimeFormat('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(date);
    } catch (error) {
      console.error('Errore formattazione data:', error, dateString);
      return 'N/D';
    }
  };

  // ========================================
  // RENDER
  // ========================================
  
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
      {/* Header Section */}
      <div className="mb-8">
        <h1 
          className="text-4xl font-bold mb-4 flex items-center gap-3" 
          style={{ color: COLORS.text }}
        >
          <Database className="h-10 w-10" style={{ color: COLORS.primary }} />
          Banca Dati Gare d'Appalto
        </h1>
        <p className="text-lg" style={{ color: COLORS.text2 }}>
          Visualizza e filtra tutte le gare raccolte
        </p>
      </div>

      {/* Statistiche */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div 
            className="rounded-xl p-6"
            style={{ 
              backgroundColor: '#FFFFFF',
              border: `1px solid ${COLORS.border}`,
              boxShadow: '0 8px 24px rgba(0,0,0,0.05)'
            }}
          >
            <div className="flex items-center gap-4">
              <div 
                className="p-3 rounded-lg"
                style={{ backgroundColor: COLORS.softBlue }}
              >
                <Database className="h-6 w-6" style={{ color: COLORS.primary }} />
              </div>
              <div>
                <div className="text-sm font-medium" style={{ color: COLORS.text2 }}>
                  Totale Gare
                </div>
                <div className="text-2xl font-bold" style={{ color: COLORS.text }}>
                  {stats.total_lotti}
                </div>
              </div>
            </div>
          </div>
          <div 
            className="rounded-xl p-6"
            style={{ 
              backgroundColor: '#FFFFFF',
              border: `1px solid ${COLORS.border}`,
              boxShadow: '0 8px 24px rgba(0,0,0,0.05)'
            }}
          >
            <div className="flex items-center gap-4">
              <div 
                className="p-3 rounded-lg"
                style={{ backgroundColor: COLORS.softBlue }}
              >
                <DollarSign className="h-6 w-6" style={{ color: COLORS.primary }} />
              </div>
              <div>
                <div className="text-sm font-medium" style={{ color: COLORS.text2 }}>
                  Valore Totale
                </div>
                <div className="text-2xl font-bold" style={{ color: COLORS.text }}>
                  {formatCurrency(stats.total_value)}
                </div>
              </div>
            </div>
          </div>
          <div 
            className="rounded-xl p-6"
            style={{ 
              backgroundColor: '#FFFFFF',
              border: `1px solid ${COLORS.border}`,
              boxShadow: '0 8px 24px rgba(0,0,0,0.05)'
            }}
          >
            <div className="flex items-center gap-4">
              <div 
                className="p-3 rounded-lg"
                style={{ backgroundColor: COLORS.softBlue }}
              >
                <DollarSign className="h-6 w-6" style={{ color: COLORS.primary }} />
              </div>
              <div>
                <div className="text-sm font-medium" style={{ color: COLORS.text2 }}>
                  Valore Medio
                </div>
                <div className="text-2xl font-bold" style={{ color: COLORS.text }}>
                  {formatCurrency(stats.avg_value)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sezione Filtri */}
      <div 
        className="rounded-2xl p-6 mb-8"
        style={{ 
          backgroundColor: '#FFFFFF',
          border: `1px solid ${COLORS.border}`,
          boxShadow: '0 8px 24px rgba(0,0,0,0.05)'
        }}
      >
        <h2 
          className="text-xl font-semibold mb-6 flex items-center gap-2"
          style={{ color: COLORS.text }}
        >
          <Filter className="h-6 w-6" style={{ color: COLORS.primary }} />
          Filtri di Ricerca
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {/* Ricerca per testo */}
          <div>
            <label 
              htmlFor="search_text"
              className="block text-sm font-semibold mb-2 flex items-center gap-2"
              style={{ color: COLORS.text }}
            >
              <Search className="h-4 w-4" style={{ color: COLORS.primary }} />
              Cerca per Titolo o Descrizione
            </label>
            <input
              type="text"
              id="search_text"
              name="search_text"
              value={filters.search_text}
              onChange={handleFilterChange}
              placeholder="Es: manutenzione, fornitura..."
              className={[
                "w-full px-3 py-2 rounded-lg border text-sm",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
              ].join(' ')}
              style={{
                borderColor: COLORS.border,
                color: COLORS.text,
              }}
            />
          </div>

          {/* Intervallo Data Scadenza */}
          <div>
            <label 
              className="block text-sm font-semibold mb-2 flex items-center gap-2"
              style={{ color: COLORS.text }}
            >
              <Calendar className="h-4 w-4" style={{ color: COLORS.primary }} />
              Data Scadenza
            </label>
            <div className="flex flex-col md:flex-row gap-2 md:items-center">
              <input
                type="date"
                id="date_from"
                name="date_from"
                value={filters.date_from}
                onChange={handleFilterChange}
                placeholder="Da"
                className={[
                  "flex-1 min-w-0 px-3 py-2 rounded-lg border text-sm",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                ].join(' ')}
                style={{
                  borderColor: COLORS.border,
                  color: COLORS.text,
                }}
              />
              <span className="hidden md:inline text-sm" style={{ color: COLORS.text2 }}>-</span>
              <input
                type="date"
                id="date_to"
                name="date_to"
                value={filters.date_to}
                onChange={handleFilterChange}
                placeholder="A"
                className={[
                  "flex-1 min-w-0 px-3 py-2 rounded-lg border text-sm",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                ].join(' ')}
                style={{
                  borderColor: COLORS.border,
                  color: COLORS.text,
                }}
                min={filters.date_from || undefined}
              />
            </div>
          </div>

          {/* Intervallo Data Pubblicazione */}
          <div>
            <label 
              className="block text-sm font-semibold mb-2 flex items-center gap-2"
              style={{ color: COLORS.text }}
            >
              <Calendar className="h-4 w-4" style={{ color: COLORS.primary }} />
              Data Pubblicazione
            </label>
            <div className="flex flex-col md:flex-row gap-2 md:items-center">
              <input
                type="date"
                id="published_from"
                name="published_from"
                value={filters.published_from}
                onChange={handleFilterChange}
                placeholder="Da"
                className={[
                  "flex-1 min-w-0 px-3 py-2 rounded-lg border text-sm",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                ].join(' ')}
                style={{
                  borderColor: COLORS.border,
                  color: COLORS.text,
                }}
              />
              <span className="hidden md:inline text-sm" style={{ color: COLORS.text2 }}>-</span>
              <input
                type="date"
                id="published_to"
                name="published_to"
                value={filters.published_to}
                onChange={handleFilterChange}
                placeholder="A"
                className={[
                  "flex-1 min-w-0 px-3 py-2 rounded-lg border text-sm",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                ].join(' ')}
                style={{
                  borderColor: COLORS.border,
                  color: COLORS.text,
                }}
                min={filters.published_from || undefined}
              />
            </div>
          </div>

          {/* Intervallo Importo - Più compatto */}
          <div className="lg:col-span-1">
            <label 
              className="block text-sm font-semibold mb-2 flex items-center gap-2"
              style={{ color: COLORS.text }}
            >
              <DollarSign className="h-4 w-4" style={{ color: COLORS.primary }} />
              Importo (€)
            </label>
            <div className="flex gap-1.5 items-center">
              <input
                type="number"
                id="value_min"
                name="value_min"
                value={filters.value_min}
                onChange={handleFilterChange}
                placeholder="Min"
                className={[
                  "w-24 px-2 py-2 rounded-lg border text-sm",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                ].join(' ')}
                style={{
                  borderColor: COLORS.border,
                  color: COLORS.text,
                }}
                min="0"
                step="1000"
              />
              <span className="text-xs" style={{ color: COLORS.text2 }}>-</span>
              <input
                type="number"
                id="value_max"
                name="value_max"
                value={filters.value_max}
                onChange={handleFilterChange}
                placeholder="Max"
                className={[
                  "w-24 px-2 py-2 rounded-lg border text-sm",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                ].join(' ')}
                style={{
                  borderColor: COLORS.border,
                  color: COLORS.text,
                }}
                min={filters.value_min || 0}
                step="1000"
              />
            </div>
          </div>

          {/* Filtro Stato Gara - Posizionato sotto Data Scadenza */}
          <div className="lg:col-start-2 lg:row-start-2">
            <label 
              htmlFor="status"
              className="block text-sm font-semibold mb-2 flex items-center gap-2"
              style={{ color: COLORS.text }}
            >
              <Tag className="h-4 w-4" style={{ color: COLORS.primary }} />
              Stato Gara
            </label>
            <select
              id="status"
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className={[
                "w-full px-3 py-2 rounded-lg border text-sm",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
              ].join(' ')}
              style={{
                borderColor: COLORS.border,
                color: COLORS.text,
              }}
            >
              <option value="">Tutte le gare</option>
              <option value="active">🟢 Attive</option>
              <option value="archived">📦 Archiviate</option>
            </select>
          </div>
        </div>

        {/* Filtro Regioni e Province */}
        <div className="mb-6">
          <RegionProvinceSelector
            selectedRegions={filters.regioni}
            selectedProvinces={filters.province}
            onRegionsChange={handleRegionsChange}
            onProvincesChange={handleProvincesChange}
          />
        </div>

        {/* Filtro Categorie CPV */}
        <div className="mb-6">
          <CPVTreeSelector
            selectedCodes={filters.cpv_codes}
            onCodesChange={handleCPVCodesChange}
            maxHeight="350px"
          />
        </div>

        {/* Filtro Categorie SOA */}
        <div className="mb-6">
          <SOASelector
            selectedSOACodes={filters.soa_codes}
            onSOACodesChange={handleSOACodesChange}
            maxHeight="350px"
          />
        </div>

        {/* Pulsanti filtri */}
        <div className="flex gap-3 justify-end pt-4 border-t" style={{ borderColor: COLORS.border }}>
          <button 
            onClick={handleResetFilters} 
            disabled={loading}
            className={[
              "inline-flex items-center px-4 py-2 rounded-lg transition-colors",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
              "disabled:opacity-50",
            ].join(' ')}
            style={{ 
              color: COLORS.text, 
              border: `1px solid ${COLORS.border}`,
              backgroundColor: 'transparent'
            }}
            onMouseEnter={(e) => {
              if (!loading) e.currentTarget.style.backgroundColor = COLORS.bg;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <X className="h-4 w-4 mr-2" />
            Reset Filtri
          </button>
          <button 
            onClick={handleApplyFilters} 
            disabled={loading}
            className={[
              "inline-flex items-center px-4 py-2 rounded-lg transition-colors",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
              "disabled:opacity-50",
            ].join(' ')}
            style={{ 
              backgroundColor: COLORS.primary,
              color: '#FFFFFF'
            }}
            onMouseEnter={(e) => {
              if (!loading) e.currentTarget.style.backgroundColor = COLORS.primaryHover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = COLORS.primary;
            }}
          >
            <Search className="h-4 w-4 mr-2" />
            Applica Filtri
          </button>
        </div>
      </div>

      {/* Risultati */}
      <div 
        className="rounded-2xl overflow-hidden"
        style={{ 
          backgroundColor: '#FFFFFF',
          border: `1px solid ${COLORS.border}`,
          boxShadow: '0 8px 24px rgba(0,0,0,0.05)'
        }}
      >
        <div 
          className="p-6 border-b"
          style={{ 
            backgroundColor: COLORS.softBlue,
            borderColor: COLORS.border 
          }}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <h2 
              className="text-xl font-semibold"
              style={{ color: COLORS.text }}
            >
              📋 Risultati
            </h2>
            <div className="text-sm" style={{ color: COLORS.text2 }}>
              {loading ? (
                <span>Caricamento...</span>
              ) : (
                <span>
                  Trovati <strong style={{ color: COLORS.text }}>{pagination.total}</strong> risultati
                  {pagination.total > 0 && (
                    <> (Pagina {pagination.page} di {pagination.total_pages})</>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Loading spinner */}
          {loading && (
            <div className="text-center py-12">
              <div
                className="animate-spin rounded-full h-12 w-12 mx-auto border-b-2 mb-4"
                style={{ borderColor: COLORS.primary }}
              ></div>
              <p style={{ color: COLORS.text2 }}>Caricamento dati...</p>
            </div>
          )}

          {/* Nessun risultato */}
          {!loading && avvisi.length === 0 && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📭</div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: COLORS.text }}>
                Nessuna gara trovata
              </h3>
              <p style={{ color: COLORS.text2 }}>
                Prova a modificare i filtri di ricerca o avvia un nuovo scraping.
              </p>
            </div>
          )}

          {/* Card degli avvisi - Una per riga */}
          {!loading && avvisi.length > 0 && (
            <div className="space-y-6">
              {avvisi.map((avviso) => {
                // Se il titolo è null, usa la descrizione come titolo
                const displayTitle = avviso.title || avviso.description || 'Avviso senza titolo';

                const goToDetails = () => {
                  // Costruisci i parametri URL con i filtri attuali
                  const params = new URLSearchParams();
                  if (pagination.page > 1) params.set('page', pagination.page);
                  if (filters.search_text) params.set('search_text', filters.search_text);
                  if (filters.date_from) params.set('date_from', filters.date_from);
                  if (filters.date_to) params.set('date_to', filters.date_to);
                  if (filters.published_from) params.set('published_from', filters.published_from);
                  if (filters.published_to) params.set('published_to', filters.published_to);
                  if (filters.value_min) params.set('value_min', filters.value_min);
                  if (filters.value_max) params.set('value_max', filters.value_max);
                  if (filters.regioni && filters.regioni.length > 0) params.set('regioni', filters.regioni.join(','));
                  if (filters.province && filters.province.length > 0) params.set('province', filters.province.join(','));
                  if (filters.cpv_codes && filters.cpv_codes.length > 0) params.set('cpv_codes', filters.cpv_codes.join(','));
                  if (filters.soa_codes && filters.soa_codes.length > 0) params.set('soa_codes', filters.soa_codes.join(','));
                  if (filters.status) params.set('status', filters.status);

                  // Salva snapshot della lista corrente per ripristino identico al ritorno dallo "Dettagli".
                  // Key basata sulla query string (stessa che verrà usata tornando a /storico-gare?...).
                  const snapshotKey = `storico_snapshot:${params.toString()}`;
                  try {
                    sessionStorage.setItem(
                      snapshotKey,
                      JSON.stringify({
                        avvisi,
                        filters,
                        pagination,
                        scrollY: window.scrollY,
                        savedAt: Date.now(),
                      })
                    );
                  } catch (_e) {
                    // Se lo storage è pieno o bloccato, continuiamo comunque con la navigazione.
                  }

                  const queryString = params.toString();
                  navigate(`/storico-gare/${avviso.id}?from=storico-gare${queryString ? '&' + queryString : ''}`);
                };
                
                return (
                <div 
                  key={avviso.id}
                  className="rounded-xl border transition-all hover:shadow-lg"
                  style={{ 
                    backgroundColor: '#FFFFFF',
                    borderColor: COLORS.border
                  }}
                >
                  <div className="p-6">
                    {/* Header (responsive): su mobile bottone va a capo in fondo */}
                    <div className="mb-3">
                      {/* Riga 1: Titolo + Badge */}
                      <div className="flex items-start justify-between gap-3">
                        <h3
                          className="text-xl font-semibold flex-1 min-w-0 break-words"
                          style={{ color: COLORS.primary }}
                        >
                          {displayTitle}
                        </h3>

                        <span
                          className="px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap flex-shrink-0"
                          style={{
                            backgroundColor: avviso.is_active ? '#D1FAE5' : COLORS.border,
                            color: avviso.is_active ? '#065F46' : COLORS.text2,
                          }}
                        >
                          {avviso.is_active ? '🟢 Attivo' : '📦 Archivato'}
                        </span>
                      </div>

                      {/* Riga 2: Bottone solo su mobile */}
                      <div className="mt-3 md:hidden">
                        <button
                          type="button"
                          onClick={goToDetails}
                          className={[
                            "w-full px-6 py-3 rounded-lg transition-colors text-sm font-semibold",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                          ].join(' ')}
                          style={{
                            backgroundColor: COLORS.primary,
                            color: '#FFFFFF',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = COLORS.primaryHover)}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = COLORS.primary)}
                        >
                          📄 Dettagli
                        </button>
                      </div>

                      {/* Desktop: badge + bottone nella stessa riga */}
                      <div className="hidden md:flex justify-end mt-0">
                        <button
                          type="button"
                          onClick={goToDetails}
                          className={[
                            "px-6 py-3 rounded-lg transition-colors text-sm font-semibold whitespace-nowrap",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                          ].join(' ')}
                          style={{
                            backgroundColor: COLORS.primary,
                            color: '#FFFFFF',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = COLORS.primaryHover)}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = COLORS.primary)}
                        >
                          📄 Dettagli
                        </button>
                      </div>
                    </div>

                    {/* Separatore leggero */}
                    <div 
                      className="mb-4"
                      style={{ 
                        borderTop: `1px solid ${COLORS.border}`,
                        marginTop: '12px',
                        marginBottom: '12px'
                      }}
                    />

                    {/* Metadati - Tutti incolonnati verticalmente */}
                    <div className="space-y-4">
                      {/* Metadati Importanti: Luogo, Valore, Scadenza */}
                      
                      {/* Luogo */}
                      {((avviso.regioni && avviso.regioni.length > 0) || (avviso.province && avviso.province.length > 0)) && (
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <MapPin className="h-4 w-4 flex-shrink-0" style={{ color: '#64748b' }} />
                            <span 
                              className="text-xs font-medium uppercase tracking-wide"
                              style={{ color: '#64748b' }}
                            >
                              Luogo
                            </span>
                          </div>
                          <div className="text-sm font-semibold truncate" style={{ color: '#1F2937' }}>
                            {avviso.province && avviso.province.length > 0 
                              ? `${avviso.province.join(', ')}${avviso.regioni && avviso.regioni.length > 0 ? ` - ${avviso.regioni.join(', ')}` : ''}`
                              : (avviso.regioni && avviso.regioni.length > 0 ? avviso.regioni.join(', ') : 'N/D')}
                          </div>
                        </div>
                      )}

                      {/* Valore Totale */}
                      {avviso.total_value && (
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <DollarSign className="h-4 w-4 flex-shrink-0" style={{ color: '#64748b' }} />
                            <span 
                              className="text-xs font-medium uppercase tracking-wide"
                              style={{ color: '#64748b' }}
                            >
                              Valore
                            </span>
                          </div>
                          <div className="text-sm font-semibold" style={{ color: '#1F2937' }}>
                            {formatCurrency(avviso.total_value)}
                          </div>
                        </div>
                      )}

                      {/* Scadenza */}
                      {avviso.deadline && (
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Calendar className="h-4 w-4 flex-shrink-0" style={{ color: '#64748b' }} />
                            <span 
                              className="text-xs font-medium uppercase tracking-wide"
                              style={{ color: '#64748b' }}
                            >
                              Scadenza
                            </span>
                          </div>
                          <div className="text-sm font-semibold" style={{ color: '#1F2937' }}>
                            {formatDate(avviso.deadline)}
                          </div>
                        </div>
                      )}

                      {/* Numero lotti */}
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Database className="h-4 w-4 flex-shrink-0" style={{ color: '#64748b' }} />
                          <span 
                            className="text-xs font-medium uppercase tracking-wide"
                            style={{ color: '#64748b' }}
                          >
                            Lotti
                          </span>
                        </div>
                        <div className="text-sm font-semibold" style={{ color: '#1F2937' }}>
                          {avviso.total_lotti}
                        </div>
                      </div>

                      {/* Data di Pubblicazione */}
                      {avviso.published_at && (
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Calendar className="h-4 w-4 flex-shrink-0" style={{ color: '#64748b' }} />
                            <span 
                              className="text-xs font-medium uppercase tracking-wide"
                              style={{ color: '#64748b' }}
                            >
                              Pubblicazione
                            </span>
                          </div>
                          <div className="text-sm font-semibold" style={{ color: '#1F2937' }}>
                            {formatDate(avviso.published_at)}
                          </div>
                        </div>
                      )}

                      {/* Buyer Name */}
                      {avviso.buyer_name && (
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Building2 className="h-4 w-4 flex-shrink-0" style={{ color: '#64748b' }} />
                            <span 
                              className="text-xs font-medium uppercase tracking-wide"
                              style={{ color: '#64748b' }}
                            >
                              Committente
                            </span>
                          </div>
                          <div className="text-sm font-semibold truncate" style={{ color: '#1F2937' }}>
                            {avviso.buyer_name}
                          </div>
                        </div>
                      )}

                      {/* Categoria CPV */}
                      {avviso.cpv_category && (
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Tag className="h-4 w-4 flex-shrink-0" style={{ color: '#64748b' }} />
                            <span 
                              className="text-xs font-medium uppercase tracking-wide"
                              style={{ color: '#64748b' }}
                            >
                              CPV
                            </span>
                          </div>
                          <div className="text-sm font-semibold truncate" style={{ color: '#1F2937' }}>
                            {avviso.cpv_category}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}

          {/* Paginazione */}
          {!loading && pagination.total_pages > 1 && (
            <div 
              className="flex items-center justify-center gap-3 pt-6 border-t"
              style={{ borderColor: COLORS.border }}
            >
              <button 
                onClick={() => handlePageChange(1)}
                disabled={pagination.page === 1}
                className={[
                  "px-3 py-2 rounded-lg transition-colors text-sm font-medium",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                ].join(' ')}
                style={{ 
                  color: COLORS.text, 
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (pagination.page !== 1) e.currentTarget.style.backgroundColor = COLORS.bg;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                ⏮️ Prima
              </button>
              
              <button 
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className={[
                  "px-3 py-2 rounded-lg transition-colors text-sm font-medium",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                ].join(' ')}
                style={{ 
                  color: COLORS.text, 
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (pagination.page !== 1) e.currentTarget.style.backgroundColor = COLORS.bg;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                ◀️ Precedente
              </button>

              <div className="px-4 py-2 text-sm" style={{ color: COLORS.text2 }}>
                Pagina <strong style={{ color: COLORS.text }}>{pagination.page}</strong> di <strong style={{ color: COLORS.text }}>{pagination.total_pages}</strong>
              </div>

              <button 
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.total_pages}
                className={[
                  "px-3 py-2 rounded-lg transition-colors text-sm font-medium",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                ].join(' ')}
                style={{ 
                  color: COLORS.text, 
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (pagination.page !== pagination.total_pages) e.currentTarget.style.backgroundColor = COLORS.bg;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Successiva ▶️
              </button>

              <button 
                onClick={() => handlePageChange(pagination.total_pages)}
                disabled={pagination.page === pagination.total_pages}
                className={[
                  "px-3 py-2 rounded-lg transition-colors text-sm font-medium",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                ].join(' ')}
                style={{ 
                  color: COLORS.text, 
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (pagination.page !== pagination.total_pages) e.currentTarget.style.backgroundColor = COLORS.bg;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Ultima ⏭️
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StoricoGarePage;

